import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { isEmpty, values, first, trim, includes } from 'lodash';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    selectBaseQueryConfigurations,
    selectExclusionRulesQueryConfigurationsState,
} from 'store/slices/queryConfigurations';
import {
    addNewDynamicSegmentSubSegment,
    changeDynamicSegmentActiveTabIndex,
    changeDynamicSegmentSubSegmentName,
    deleteDynamicSegmentSubSegment,
    selectDynamicSegmentFiltersData,
    selectDynamicSegmentActiveTabIndex,
} from 'store/slices/dynamicSegment';
// Import UI Components
import {
    ModuleTitle,
    Empty,
    Icon,
    ExtendedInput,
    Tabs,
    Tab,
    Tooltip,
    Button,
    Divider,
    Modal,
    BusyLoader,
} from '@geneui/components';
// Import Components
import FilterSchemaList from './FilterSchemaList';
import SegmentReportPreviewModal from 'components/SegmentReportPreviewModal';
import SegmentClientsCount from 'components/common/SegmentClientsCount';
// Import Services
import { createQueryConfigurationsFromFilters } from 'services/segment';
import { SegmentHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useReportColumns from 'hooks/UseReportColumns';
import { useValidator } from 'hooks';
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
// Import Constants
import { DefaultColumnsReport } from '../constants';
import { l, ModalsClassNames, SEGMENT_FILTER_APPLICATION_COLUMN_ID } from 'constants/common';
// Import Enums
import { SegmentClientsCountRequestEnum } from 'enums';

const TabsQuantity = 5;
const MaxLengthForImmediateDispatch = 14;

const FiltersContainer = (props) => {
    const {
        handleChangeActiveList,
        readOnly,
        setReadOnly,
        resetActiveList,
        editingFilter,
        setEditingFilter,
        columnsReport,
        pageType,
        setTabActiveId,
        tabActiveId,
        editModeCancelHandler,
        unifiedCurrency,
        hideFilterContainerTabs = false,
        isExclusionRules = false,
    } = props;
    const { t } = useTranslation(['translations']);

    const { doPostRequest } = useRequest();
    const { getAutocompleteRequest, cancelRequest } = useAutocompleteRequest();
    const dispatch = useAppDispatch();
    const validator = useValidator();
    const intervalToken = useRef<Nullable<NodeJS.Timeout>>(null);

    const dynamicSegmentFiltersData = useAppSelector(selectDynamicSegmentFiltersData);
    const queryConfigurations = useAppSelector(
        isExclusionRules ? selectExclusionRulesQueryConfigurationsState : selectBaseQueryConfigurations,
    );
    const tabActiveIndex = useAppSelector(selectDynamicSegmentActiveTabIndex);

    const [clientsData, setClientsData] = useState<Nullable<AnyObject>>(null);
    const [usedInSegmentsTabsLabels, setUsedInSegmentsTabsLabels] = useState('');
    const [subSegmentNameValue, setSubSegmentNameValue] = useState('');
    const [isOpenedWarningModal, setIsOpenedWarningModal] = useState(false);
    const [isVisibleModal, setIsVisibleModal] = useState(false);
    const [isVisibleInfoMessage, setIsVisibleInfoMessage] = useState(false);

    const segmentClientsCountFiltersConfig = useMemo(
        () => createQueryConfigurationsFromFilters(dynamicSegmentFiltersData),
        [dynamicSegmentFiltersData],
    );

    const dynamicSegmentFiltersDataValues = useMemo(
        () => values(dynamicSegmentFiltersData),
        [dynamicSegmentFiltersData],
    );

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_filteredColumns, isLoadingReportColumns] = useReportColumns();

    const { getSegmentCount, getDynamicSegmentPreview } = useRef({
        // eslint-disable-next-line no-restricted-syntax -- never sent, only cancelled (dead code, remove separately)
        getSegmentCount: SegmentHttpService.getSegmentCount(),
        getDynamicSegmentPreview: SegmentHttpService.getDynamicSegmentPreview,
    }).current;

    useEffect(() => {
        !isEmpty(dynamicSegmentFiltersData) && setSubSegmentNameValue(dynamicSegmentFiltersData[tabActiveId]?.name);
    }, [tabActiveId, dynamicSegmentFiltersData]);

    useEffect(() => {
        validator({ QueryConfigurations: dynamicSegmentFiltersData });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicSegmentFiltersData]);

    const cleanUp = () => {
        return () => {
            dispatch(changeDynamicSegmentActiveTabIndex(0));
            getSegmentCount.cancel('FilterContainer:getSegmentCount');
            cancelRequest('FilterContainer:getDynamicSegmentPreview');
        };
    };

    const closeModal = () => {
        cancelRequest('FilterContainer:getDynamicSegmentPreview');
        setIsVisibleInfoMessage(false);
        setIsVisibleModal(false);
        setClientsData(null);
    };

    const generateSubSegment = (name, index) => {
        const uuid = uuidv4();
        return {
            [uuid]: {
                id: uuid,
                name: name,
                seqId: index,
                filters: {},
            },
        };
    };

    const getSubSegmentName = useCallback(() => {
        const names = dynamicSegmentFiltersDataValues.map((segment) => segment.name);
        for (let i = 1; i < TabsQuantity; i++) {
            if (!includes(names, `${t(l.SubSegment)} ${i}`)) {
                return `${t(l.SubSegment)} ${i}`;
            }
        }
    }, [dynamicSegmentFiltersDataValues, t]);

    const addSubSegmentClickHandler = () => {
        const highestIndex = Math.max(...dynamicSegmentFiltersDataValues.map((el) => el.seqId), 0);
        const name = getSubSegmentName();
        dispatch(addNewDynamicSegmentSubSegment(generateSubSegment(name, highestIndex + 1)));
    };

    const tabChangeHandler = (seqId, key) => {
        editModeCancelHandler();
        dispatch(changeDynamicSegmentActiveTabIndex(seqId));
        setTabActiveId(key);
    };

    const getUsedInSegmentNames = (segments, id, seqId) => {
        return segments
            .filter((s) => s.seqId < seqId)
            .filter((segment) =>
                values(segment.filters).some((group) =>
                    group.some(
                        (f) =>
                            f.comparison.applicationColumnId === SEGMENT_FILTER_APPLICATION_COLUMN_ID &&
                            f.Value.Data.Value.includes(id),
                    ),
                ),
            )
            .map((segment) => segment.name);
    };

    const deleteSubSegmentHandler = (id, seqId) => {
        const mainSegment = dynamicSegmentFiltersDataValues.find((obj) => obj.isMain);
        dispatch(changeDynamicSegmentActiveTabIndex(0));
        setTabActiveId(mainSegment?.id);

        const usedInSegmentsNames = getUsedInSegmentNames(dynamicSegmentFiltersDataValues, id, seqId);
        isEmpty(usedInSegmentsNames) && dispatch(deleteDynamicSegmentSubSegment(id));
        setIsOpenedWarningModal(!isEmpty(usedInSegmentsNames));
        !isEmpty(usedInSegmentsNames) && setUsedInSegmentsTabsLabels(usedInSegmentsNames.join());
    };

    const isSubSegmentValid = (subSegment) => {
        return !isEmpty(subSegment.name) && !isEmpty(subSegment.filters);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const isValidDynamicSegmentsFiltersData = useMemo(() => {
        return dynamicSegmentFiltersDataValues.every((s) => !isEmpty(s.filters) && !isEmpty(s.name));
    }, [dynamicSegmentFiltersDataValues]);

    const content = useCallback(
        (subSegment) => {
            const dispatchNameInputChange = (value, id) => {
                dispatch(changeDynamicSegmentSubSegmentName({ value, id }));
            };

            const inputNameChangeHandler = (value, id) => {
                setSubSegmentNameValue(value);
                if (intervalToken.current) {
                    clearTimeout(intervalToken.current);
                }
                if (trim(value) === '' || value.length < MaxLengthForImmediateDispatch) {
                    dispatchNameInputChange(value, id);
                } else {
                    intervalToken.current = setTimeout(() => {
                        dispatchNameInputChange(value, id);
                    }, 1000);
                }
            };

            const nameInputOnBlurHandler = (value, id) => {
                if (value.trim() === '') {
                    const name = getSubSegmentName();
                    dispatchNameInputChange(name, id);
                }
                dynamicSegmentFiltersDataValues.forEach((segment) => {
                    if (value === segment.name && id !== segment.id) {
                        const newName = getSubSegmentName();
                        dispatchNameInputChange(newName, id);
                    }
                });
            };

            const getSegmentReports = (config) => {
                const requestObj = getAutocompleteRequest(getDynamicSegmentPreview);

                doPostRequest(requestObj.request, {
                    requestBody: config,
                    successCallback: (Data) => {
                        setClientsData(first(Data.Data));
                    },
                    errorCallback: () => {
                        setIsVisibleInfoMessage(true);
                    },
                });
            };

            const handleClickPreview = () => {
                const config = createQueryConfigurationsFromFilters(
                    dynamicSegmentFiltersData,
                    columnsReport,
                    unifiedCurrency,
                );
                setIsVisibleModal(true);
                getSegmentReports(config);
            };
            return (
                <>
                    {subSegment?.isMain && (
                        <div className="crm-dynamic-segment-header crm-justify-content-between crm-full-width p-20">
                            <div className="total-calculated-block-inner">
                                <span>
                                    {t(l.Count)} / {t(l.Total)}
                                </span>
                                <SegmentClientsCount
                                    isDisabled={!isValidDynamicSegmentsFiltersData}
                                    config={{
                                        type: SegmentClientsCountRequestEnum.Filter,
                                        filtersConfig: segmentClientsCountFiltersConfig,
                                    }}
                                />
                            </div>
                            <div className="crm-segment-filters-header-blocks">
                                <Divider withSpace={true} type="vertical" />
                                <Button
                                    color="primary"
                                    appearance="minimal"
                                    icon="bc-icon-view-doc"
                                    className="ml-10"
                                    disabled={!isValidDynamicSegmentsFiltersData || isLoadingReportColumns}
                                    onClick={handleClickPreview}
                                />
                            </div>
                        </div>
                    )}
                    {!subSegment?.isMain && (
                        <div className="dynamic-segment-name-input-container">
                            <ExtendedInput
                                type="text"
                                placeholder={l.Name}
                                value={subSegmentNameValue}
                                onChange={(e) => inputNameChangeHandler(e.target.value, subSegment.id)}
                                appearance="outline"
                                label={l.Name}
                                labelAppearance="swap"
                                onBlur={(e) => nameInputOnBlurHandler(e.target.value, subSegment.id)}
                            />
                        </div>
                    )}
                    {isEmpty(subSegment.filters) ? (
                        <div className="create-dynamic-segment-p-w-c-i-c-content-column">
                            <div className="create-dynamic-segment-p-w-c-i-c-content-c-inner">
                                <div className="crm-scroll-holder">
                                    <div className="crm-scrollable-container">
                                        <div className="empty-container">
                                            <Empty
                                                appearance="greyscale"
                                                withImage={true}
                                                title={t(l.NoSegmentToDisplay)}
                                                subTitle={t(
                                                    l.YourSegmentsWillAppearInThisContainerAsSoonAsYouSelectFilters,
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="create-dynamic-segment-p-w-c-i-c-content-column">
                            <div className="create-dynamic-segment-p-w-c-i-c-content-c-inner">
                                <div className="crm-scroll-holder">
                                    <div
                                        className={classNames('crm-scrollable-container', {
                                            'crm-editable-state': readOnly,
                                        })}
                                    >
                                        <FilterSchemaList
                                            filtersData={subSegment}
                                            queryConfigurations={queryConfigurations}
                                            handleChangeActiveList={handleChangeActiveList}
                                            readOnly={readOnly}
                                            setReadOnly={setReadOnly}
                                            resetActiveList={resetActiveList}
                                            editingFilter={editingFilter}
                                            setEditingFilter={setEditingFilter}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            );
        },
        [
            columnsReport,
            dispatch,
            doPostRequest,
            dynamicSegmentFiltersData,
            dynamicSegmentFiltersDataValues,
            editingFilter,
            getAutocompleteRequest,
            getDynamicSegmentPreview,
            getSubSegmentName,
            handleChangeActiveList,
            isLoadingReportColumns,
            isValidDynamicSegmentsFiltersData,
            queryConfigurations,
            readOnly,
            resetActiveList,
            segmentClientsCountFiltersConfig,
            setEditingFilter,
            setReadOnly,
            subSegmentNameValue,
            t,
            unifiedCurrency,
        ],
    );

    return (
        <>
            <div className="create-dynamic-segment-p-w-c-i-column-head">
                <ModuleTitle
                    title={t(l.FiltersContainer)}
                    titleIcon="bc-icon-info"
                    titleIconTooltip={t(l.DynamicSegmentFiltersContainerTooltipText)}
                />
            </div>
            {isEmpty(dynamicSegmentFiltersData) ? (
                <BusyLoader
                    isBusy
                    type="spinner"
                    spinnerSize="big"
                    className="crm-full-width crm-justify-content-center"
                />
            ) : (
                <div className="dynamic-segment-tabs-container">
                    {hideFilterContainerTabs ? (
                        <>
                            {dynamicSegmentFiltersDataValues.map((subSegment) => (
                                <div key={subSegment.id} className="full-height">
                                    {content(subSegment)}
                                </div>
                            ))}
                        </>
                    ) : (
                        <Tabs
                            type="basic"
                            activeKey={tabActiveIndex}
                            actions={
                                <div className="substep-tabs-actions-wrapper">
                                    <Icon
                                        type="bc-icon-add"
                                        onClick={addSubSegmentClickHandler}
                                        disabled={dynamicSegmentFiltersDataValues.length >= TabsQuantity}
                                    />
                                </div>
                            }
                        >
                            {dynamicSegmentFiltersDataValues.map((subSegment) => {
                                return (
                                    <Tab
                                        key={subSegment.seqId}
                                        className={`${+tabActiveIndex === subSegment.seqId ? 'active' : ''} ${
                                            isSubSegmentValid(subSegment) ? '' : 'invalid-tab'
                                        }`}
                                        title={
                                            <Tooltip text={subSegment.name}>
                                                <div className="ellipsis-text">
                                                    <button
                                                        onClick={() =>
                                                            tabChangeHandler(subSegment.seqId, subSegment.id)
                                                        }
                                                    >
                                                        <p>
                                                            {+tabActiveIndex === subSegment.seqId
                                                                ? subSegment.name
                                                                : subSegment.name.slice(0, 4)}
                                                        </p>
                                                    </button>
                                                    {!subSegment?.isMain && (
                                                        <Icon
                                                            type="bc-icon-clear-small"
                                                            onClick={() =>
                                                                deleteSubSegmentHandler(subSegment.id, subSegment.seqId)
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </Tooltip>
                                        }
                                    >
                                        {content(subSegment)}
                                    </Tab>
                                );
                            })}
                        </Tabs>
                    )}
                </div>
            )}

            {isVisibleModal && (
                <SegmentReportPreviewModal
                    isVisible={isVisibleModal}
                    closeHandler={closeModal}
                    clientsData={clientsData}
                    isVisibleInfoMessage={isVisibleInfoMessage}
                    pageType={pageType}
                />
            )}
            {isOpenedWarningModal && (
                <Modal
                    title={l.DeleteSubSegmentWarningMessage}
                    position="top"
                    className={ModalsClassNames.Warning}
                    appearance="compact"
                    visible={isOpenedWarningModal}
                    size="content-size"
                    closable={false}
                    closeOnClickOutside={true}
                    onCancel={() => setIsOpenedWarningModal(false)}
                    footer={
                        <Button color="primary" appearance="minimal" onClick={() => setIsOpenedWarningModal(false)}>
                            {l.Close}
                        </Button>
                    }
                >
                    <div className="crm-circle-icon-cnt">
                        <Icon type={'bc-icon-info-48'} />
                    </div>
                    <span>{t(l.SubSegmentUsedInMessage, { usedInSegments: usedInSegmentsTabsLabels })}</span>
                </Modal>
            )}
        </>
    );
};

FiltersContainer.propTypes = {
    queryConfigurations: PropTypes.object,
    handleChangeActiveList: PropTypes.func,
    readOnly: PropTypes.bool,
    setReadOnly: PropTypes.func,
    resetActiveList: PropTypes.func,
    editingFilter: PropTypes.object,
    setEditingFilter: PropTypes.func,
    columnsReport: PropTypes.array,
    pageType: PropTypes.string,
    tabActiveId: PropTypes.string.isRequired,
    setTabActiveId: PropTypes.func.isRequired,
    editModeCancelHandler: PropTypes.func,
    unifiedCurrency: PropTypes.string,
    hideFilterContainerTabs: PropTypes.bool,
    isExclusionRules: PropTypes.bool,
};

FiltersContainer.defaultProps = {
    filtersList: [],
    readOnly: false,
    columnsReport: DefaultColumnsReport,
    hideFilterContainerTabs: false,
    isExclusionRules: false,
};

export default FiltersContainer;
